import React, { lazy, ReactNode, Suspense, useEffect, useState, useRef, useLayoutEffect } from 'react';
import Api from './libs/api';
import axios from 'axios';
import { usePaymentExit } from './queries/payment.query';

import { analytics, initAnalytics } from './firebase';
import { logEvent } from 'firebase/analytics';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useRecoilState, useSetRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import {
	GoldBoxPrice,
	DeliveryPrice,
	DecompositionRate,
	InspectionAtom,
	IsLoginAtom,
	IsOverlapAtom,
	UserLocationAtom,
	UserAtom,
	NickChangeCount,
	DeliveryRequest,
	BoxEventItem,
} from './Recoil/Atoms/UserState';
import {
	LoginAtom,
	AlertSettingAtom,
	AlertSettingContentsAtom,
	ImageLargeViewAtom,
	ImageLargeViewUrlAtom,
	SetProfileAtom,
	ToastAtom,
	ToastTypeAtom,
	ToastContentsAtom,
	PushAtom,
	GuideAtom,
	NeedUpdateAtom,
} from './Recoil/Atoms/PopupState';

import * as S from './Styles/Style';
import Modal from './Components/Modal';
import Toast from './Components/Toast';
import AlertSetting from './Components/AlertSetting';
import SetProfile from './Components/SetProfile';
import ImageLargeView from './Components/ImageLargeView';
import Util from './Store/Util';
import Guide from './Components/Guide';
import Loading from './Components/atoms/Loading';
import { endSpan, fallbackSpan, useSpanSentry } from './libs/sentry';

const retryHandlers: Record<string, () => void> = {};

const lazyWithRetry = (importFn: () => Promise<any>, page: string) =>
	lazy(async () => {
		const LOAD_TIMEOUT = 15000;
		const RETRY_TIMEOUT = 8000;
		const doImport = (timeout: number) =>
			Promise.race([
				importFn(),
				new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), timeout)),
			]);

		const tryImport = async (): Promise<any> => {
			try {
				return await doImport(LOAD_TIMEOUT);
			} catch (error) {
				try {
					return await doImport(RETRY_TIMEOUT);
				} catch (retryError) {
					if (window.ReactNativeWebView) {
						window.ReactNativeWebView.postMessage(
							JSON.stringify({
								type: 'NETWORK_ERROR',
								Page: page,
							})
						);
					}
					return new Promise((resolve) => {
						retryHandlers[page] = () => {
							delete retryHandlers[page];
							resolve(tryImport());
						};
					});
				}
			}
		};

		return tryImport();
	});

const MAIN001001 = lazyWithRetry(() => import('./Screens/MAIN001001'), 'MAIN001001');
const NOTI001001 = lazyWithRetry(() => import('./Screens/NOTI001001'), 'NOTI001001');
const MEMB001001 = lazyWithRetry(() => import('./Screens/MEMB001001'), 'MEMB001001');
const MEMB001002 = lazyWithRetry(() => import('./Screens/MEMB001002'), 'MEMB001002');
const MEMB003001 = lazyWithRetry(() => import('./Screens/MEMB003001'), 'MEMB003001');
const BUPO001001 = lazyWithRetry(() => import('./Screens/BUPO001001'), 'BUPO001001');
const BUPO001002 = lazyWithRetry(() => import('./Screens/BUPO001002'), 'BUPO001002');
const BUPO001003 = lazyWithRetry(() => import('./Screens/BUPO001003'), 'BUPO001003');
const POIN001001 = lazyWithRetry(() => import('./Screens/POIN001001'), 'POIN001001');
const NEWS001001 = lazyWithRetry(() => import('./Screens/NEWS001001'), 'NEWS001001');
const EXCH001001 = lazyWithRetry(() => import('./Screens/EXCH001001'), 'EXCH001001');
const DLVR001001 = lazyWithRetry(() => import('./Screens/DLVR001001'), 'DLVR001001');
const DLVR002001 = lazyWithRetry(() => import('./Screens/DLVR002001'), 'DLVR002001');
const MYME001001 = lazyWithRetry(() => import('./Screens/MYME001001'), 'MYME001001');
const MYME001002 = lazyWithRetry(() => import('./Screens/MYME001002'), 'MYME001002');
const HELP005001 = lazyWithRetry(() => import('./Screens/HELP005001'), 'HELP005001');
const HELP005002 = lazyWithRetry(() => import('./Screens/HELP005002'), 'HELP005002');
const MYME002001 = lazyWithRetry(() => import('./Screens/MYME002001'), 'MYME002001');
const TRSR001001 = lazyWithRetry(() => import('./Screens/TRSR001001'), 'TRSR001001');
const TRSR001002 = lazyWithRetry(() => import('./Screens/TRSR001002'), 'TRSR001002');
const UPGD001001 = lazyWithRetry(() => import('./Screens/UPGD001001'), 'UPGD001001');
const HELP001001 = lazyWithRetry(() => import('./Screens/HELP001001'), 'HELP001001');
const HELP002001 = lazyWithRetry(() => import('./Screens/HELP002001'), 'HELP002001');
const HELP006001 = lazyWithRetry(() => import('./Screens/HELP006001'), 'HELP006001');
const HELP004001 = lazyWithRetry(() => import('./Screens/HELP004001'), 'HELP004001');
const HELP004002 = lazyWithRetry(() => import('./Screens/HELP004002'), 'HELP004002');
const HELP004003 = lazyWithRetry(() => import('./Screens/HELP004003'), 'HELP004003');
const HELP003001 = lazyWithRetry(() => import('./Screens/HELP003001'), 'HELP003001');
const HELP007001 = lazyWithRetry(() => import('./Screens/HELP007001'), 'HELP007001');
const HELP007002 = lazyWithRetry(() => import('./Screens/HELP007002'), 'HELP007002');
const HELP007003 = lazyWithRetry(() => import('./Screens/HELP007003'), 'HELP007003');
const HELP008001 = lazyWithRetry(() => import('./Screens/HELP008001'), 'HELP008001');

const Fallback = () => {
	useLayoutEffect(() => {
		fallbackSpan();
	}, []);
	return <Loading />;
};

const SentryTrace = ({ children }: { children: ReactNode }) => {
	const location = useLocation();
	useEffect(() => {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				endSpan();
			});
		});
	}, [location.key]);
	return <>{children}</>;
};
class ChunkErrorBoundary extends React.Component<{ children: ReactNode }, { hasError: boolean }> {
	state = { hasError: false };
	static getDerivedStateFromError() {
		return { hasError: true };
	}
	render() {
		if (this.state.hasError) return <Fallback />;
		return this.props.children;
	}
}

const WithSuspense = (element: ReactNode) => {
	return (
		<SentryTrace>
			<ChunkErrorBoundary>
				<Suspense fallback={<Fallback />}>{element}</Suspense>
			</ChunkErrorBoundary>
		</SentryTrace>
	);
};

const App = () => {
	const adminApiUrl = process.env.REACT_APP_ADMIN_API_URL;
	const apiUrl = process.env.REACT_APP_API_URL;

	useSpanSentry();

	const { paymentExit } = usePaymentExit();

	// 유저 정보
	const setIsLogin = useSetRecoilState(IsLoginAtom);
	const [_isInspection, setInspection] = useRecoilState(InspectionAtom);
	const [userState, setUserState] = useRecoilState(UserAtom);
	const [_userLocation, setUserLocation] = useRecoilState(UserLocationAtom);
	const resetUserState = useResetRecoilState(UserAtom);
	const setNickChangeCount = useSetRecoilState(NickChangeCount);
	const loadingRef = useRef<boolean>(false);
	const isFirstLogin = useRef<boolean>(false);
	// 가격정보
	const setGoldBoxPrice = useSetRecoilState(GoldBoxPrice);
	const setBoxEvent = useSetRecoilState(BoxEventItem);
	const setDeliveryPrice = useSetRecoilState(DeliveryPrice);
	const setDecompositionRate = useSetRecoilState(DecompositionRate);
	// 팝업
	const setOpenToast = useSetRecoilState(ToastAtom);
	const setToastType = useSetRecoilState<number>(ToastTypeAtom);
	const setToastContents = useSetRecoilState<string>(ToastContentsAtom);
	// 배송 요청 완료
	const [_isOpenDeliveryRequest, setOpenDeliveryRequest] = useRecoilState<boolean>(DeliveryRequest);
	const [isOpenSetProfile, setOpenSetProfile] = useRecoilState<boolean>(SetProfileAtom); // 최초 프로필 설정 모달
	const [pushSendYn, setPushSendYn] = useState<boolean>(false); // 광고성 푸시 동의 여부
	const [_nick, setNick] = useState<string>('');
	const [_profile, setProfile] = useState<string | ArrayBuffer>('');
	const [isOpenGuide, setOpenGuide] = useRecoilState<boolean>(GuideAtom); // 이용가이드
	const [isOpenPush, setOpenPush] = useRecoilState<boolean>(PushAtom); // 알림 동의
	const [alertSettingContents, setAlertSettingContents] = useRecoilState(AlertSettingContentsAtom);
	const [changePushSendYn, setChangePushSendYn] = useState<boolean>(true); // 광고성 푸시 동의 여부

	const [isOpenNeedUpdate, setOpenNeedUpdate] = useRecoilState<boolean>(NeedUpdateAtom); // 업데이트 안내
	const needUpdateUrl = useRef<string>(''); // 업데이트 url
	const [isOpenImageLargeView, setOpenImageLargeView] = useRecoilState<boolean>(ImageLargeViewAtom); // 이미지 크게보기
	const imageLargeViewUrl = useRecoilValue(ImageLargeViewUrlAtom);
	const [isOpenUpdate, _setOpenUpdate] = useState<boolean>(false); // 업데이트 안내
	const [isOpenOverlap, setOpenOverlap] = useRecoilState<boolean>(IsOverlapAtom); // 중복 로그인
	const [isOpenAlertSetting, setOpenAlertSetting] = useRecoilState<boolean>(AlertSettingAtom);
	const [isOpenAlert, setOpenAlert] = useState<boolean>(false); // 재가입 제한
	const [canJoinDt, setCanJoinDt] = useState<string>(''); // 재가입 제한 일자
	const [isOpenLogin, setOpenLogin] = useRecoilState<boolean>(LoginAtom);
	const navigate = useNavigate();
	// 결제 요청 취소
	const [isOpenPaymentCancel, setOpenPaymentCancel] = useState(false);
	//
	const [isOpenSetting, setOpenSetting] = useState(false);

	// 회원정보 조회
	const memberInfoSelect = async () => {
		if (loadingRef.current) return;
		if (localStorage.getItem('token') == null) {
			setIsLogin(false);
			resetUserState();
			return false;
		}
		loadingRef.current = true;
		await Api.get<any>('/member/info/memberInfoSelect').then((res) => {
			if (res.data !== undefined) {
				const data = res.data.data;
				if (data.member == null) return;
				// 약관 미동의 회원
				if (data.member.stateCode === '01' || data.member.stateCode === '02') {
				} else {
					setIsLogin(true);
					setUserState(data.member);
					setNickChangeCount(data.nickChangeCount);
					setNick(data.member.nick); // 최초 프로필 설정 모달 내 기본값
					setProfile(data.member.profileImageUrl);
					if (data.member.stateCode === '03') {
						// 최초로그인 닉네임 설정 팝업 생성
						setOpenSetProfile(true);
						setPushSendYn(data.member.pushSendYn == 1);
					}
				}
			} else {
				setIsLogin(false);
				resetUserState();
			}
		});
		loadingRef.current = false;
	};
	// 가격 정보 조회
	const setPrice = async () => {
		await Api.get<any>('/common/list', { codeKey: 'goldBoxPrice' }).then((res) => {
			if (res.data !== undefined) {
				const data = res.data.data;
				setGoldBoxPrice(data[0].codeValue);
			}
		});
		await Api.get<any>('/common/list', { codeKey: 'deliveryPrice' }).then((res) => {
			if (res.data !== undefined) {
				const data = res.data.data;
				setDeliveryPrice(data[0].codeValue);
				// 도서산간 1
			}
		});
		await Api.get<any>('/common/list', { codeKey: 'decompose' }).then((res) => {
			if (res.data !== undefined) {
				const data = res.data.data;
				setDecompositionRate(data[0].codeValue / 100);
			}
		});

		await Api.get<any>('/event/getActiveEvent', {
			params: { type: 'BOX' },
		}).then((res) => {
			if (res.data?.data !== undefined) {
				setBoxEvent(res.data?.data);
			}
		});
	};
	// 점검여부 체크
	const checkInspection = async () => {
		await axios.get(adminApiUrl + '/common/inspection').then((res) => {
			if (res.data !== undefined) {
				setInspection(res.data.data);
			}
		});
	};
	// 알림 수신 설정
	const alertSetting = async (agree: boolean) => {
		if (loadingRef.current) return;
		loadingRef.current = true;
		await Api.patch(`/member/info/memberInfoChange`, {
			pushSendYn: agree ? 1 : 0,
		}).then((res) => {
			if (res.data !== undefined) {
				if (res.data.statusCode === 200) {
					setAlertSettingContents({
						agree: agree,
						type: 'pushSendYn',
						explain: true,
					});
					setOpenAlertSetting(true);
					setOpenPush(false);
					isFirstLogin.current = false;
					postMessage('NOFIRSTLOGIN');
				}
			} else {
				alert('fail');
			}
		});
		loadingRef.current = false;
	};

	useEffect(() => {
		memberInfoSelect();
		checkInspection();
	}, [isOpenSetProfile, navigate]);

	useEffect(() => {
		// 개발앱 여부 확인
		if (apiUrl.includes('dev')) {
		}
		setPrice(); // 가격 셋팅

		// google analytics 초기화
		initAnalytics();

		const { ReactNativeWebView } = window; // web 으로 접속 시 null, WebView로 접속 시에만 있음
		if (ReactNativeWebView) {
			postMessage('START');
		}
	}, []);

	const location = useLocation();
	useEffect(() => {
		// 회원가입 시, GA4 이벤트 발생
		logEvent(analytics, 'page_view', {
			page_path: location.pathname,
			page_location: window.location.href,
			page_title: document.title,
		});
	}, [location]);

	// TODO: 업데이트 중 팝업 생성 여부
	// setOpenToast(true);
	const listener = async (e) => {
		if (e.origin == 'http://192.168.0.167:3000') return;
		if (e.origin == 'https://dev.service.go-pang.com') return;
		if (typeof e.data == 'object') return;
		const dataParse = JSON.parse(e.data);
		let data;

		if (typeof dataParse == 'object') {
			data = dataParse;
		} else {
			data = JSON.parse(dataParse);
		}
		if (data.needsUpdate == true && process.env.REACT_APP_IS_PRODUCTION) {
			// 앱 업데이트 필요
			// 업데이트 안내 팝업 생성
			setOpenNeedUpdate(true);
			needUpdateUrl.current = data.url;
		}
		if (data.isFirstGuide == true) {
			// 앱 최초 설치 회원
			// 이용가이드 팝업 생성
			setOpenGuide(true);
		}
		if (data.isFirstLogin == true) {
			// 앱 최초 설치 회원

			// 회원가입 시, GA4 이벤트 발생
			logEvent(analytics, 'sign_up', { os: data.OS });

			// 푸시 동의 설정 팝업 생성
			isFirstLogin.current = true;
		}
		if (data.name !== undefined && data.name !== '') {
			// 애플 회원가입인 경우 이름 자동입력
			setUserState({ ...userState, name: data.name });
		}
		if (data.token !== undefined && data.token !== '') {
			// 최초 진입 로그인 여부 확인
			localStorage.setItem(
				'token',
				JSON.stringify({
					accessToken: data.token.accessToken,
					refreshToken: data.token.refreshToken,
				})
			);
			memberInfoSelect();
			checkInspection();
		}
		if (data.OS !== undefined) {
			// OS 확인(로그인 구분)
			localStorage.setItem('OS', data.OS);
		}
		if (data.version !== undefined) {
			// 앱 버전 확인
			localStorage.setItem('version', data.version);
		}
		if (data.buildNumber !== undefined) {
			// 앱 버전 확인(빌드 번호)
			localStorage.setItem('buildNumber', data.buildNumber);
		}
		if (data?.accessToken !== undefined) {
			// 로그인 완료 응답인 경우
			localStorage.setItem(
				'token',
				JSON.stringify({
					accessToken: data.accessToken,
					refreshToken: data.refreshToken,
				})
			);
			if (data.stateCode === '01' || data.stateCode === '02') {
				// 약관 미동의 회원
				navigate('/MEMB001002');
			} else if (data.stateCode === '03') {
				// 닉네임 미설정 회원
				setOpenSetProfile(true);
				navigate('/', { replace: true });
			} else if (data.stateCode == '11') {
				// 최초 실행이며 푸시 동의 되어있지 않으면 팝업 생성
				isFirstLogin.current && data.pushSendYn !== 1 && setOpenPush(true);
				// 정상 회원
				navigate('/', { replace: true });
			} else if (data.stateCode == '-999' || data.stateCode == '-900') {
				// 탈퇴 회원
			} else if (data.stateCode == '-10' || data.stateCode == '-30') {
				// 제한 회원
			} else if (data.stateCode == '12') {
				// 테스터
				navigate('/', { replace: true });
			} else {
			}
		} else if (data.code == '-900') {
			// 재가입 제한 회원인 경우
			setCanJoinDt(data.canJoinDt);
			setOpenAlert(true);
		} else if (data.resultMsg == 'SUCCESS') {
			// 결제 완료 응답인 경우
			if (data.type == 'box') {
				// 상자구매
				setToastType(0);
				setToastContents('Peti harta karun akan Anda terima dalam 5 menit setelah pembayaran berhasil.');
				setOpenToast(true);
				navigate('/', { replace: true });
			} else if (data.type == 'delivery') {
				// 배송요청
				setOpenDeliveryRequest(true);
			}
		} else if (data.type == 'overlap') {
			// 중복로그인
			localStorage.removeItem('token');
			setToastType(0);
			setToastContents('Anda telah log in di perangkat lain.');
			setOpenToast(true);
			memberInfoSelect();
			postMessage('LOGOUT');
		} else if (data.type == 'paymentCancel') {
			// 결제 요청 취소: 해당 결제 요청 건(ptIdx)을 이탈 상태로 업데이트
			if (data.ptIdx) {
				await paymentExit(data.ptIdx);
			}
			if (data.division == 'delivery') {
				navigate('/MYME002001');
				// await Api.patch(
				// 	"/member/info/memberPresentDeliveryInsertCancel",
				// 	{
				// 		pumIdx: data.pumIdx,
				// 	}
				// ).then((res) => {
				// 	if (res.data !== undefined) {
				// 	} else {
				// 		alert("fail");
				// 	}
				// 	// TODO: 취소실패 구분
				// });
			} else {
				setOpenPaymentCancel(true);
			}
		} else if (data.type == 'location') {
			if (data.position.latitude == null || data.position.latitude == null) {
				setOpenSetting(true);
			}
			setUserLocation({
				latitude: data.position.latitude,
				longitude: data.position.longitude,
			});
		} else if (data.type === 'RETRY_LAZY_LOAD') {
			if (data.Page && retryHandlers[data.Page]) {
				retryHandlers[data.Page]();
			} else {
				Object.values(retryHandlers).forEach((handler) => handler());
			}
		}
	};
	// 앱으로 메세지 전송
	const postMessage = (type: string) => {
		if (typeof window == undefined || !window?.ReactNativeWebView) {
			return;
		} else {
			window.ReactNativeWebView?.postMessage(JSON.stringify({ type: type }));
		}
	};
	useEffect(() => {
		document.addEventListener('message', listener);
		window.addEventListener('message', listener);
		return () => {
			document.removeEventListener('message', listener);
			window.removeEventListener('message', listener);
		};
	}, []);

	return (
		<>
			<Routes>
				{/* Main */}
				<Route path="/" element={WithSuspense(<MAIN001001 />)} />
				{/* 점검 */}
				{/* <Route path="/NOTI001001" element={WithSuspense(<NOTI001001 />}) /> */}
				{/* 로그인 */}
				<Route path="/MEMB001001" element={WithSuspense(<MEMB001001 />)} />
				{/* 번호인증 */}
				<Route path="/MEMB001002" element={WithSuspense(<MEMB001002 />)} />
				{/* 약관동의 */}
				{/* <Route path="/MEMB002001" element={WithSuspense(<MEMB002001 />}) /> */}
				{/* 회원탈퇴 */}
				<Route path="/MEMB003001" element={WithSuspense(<MEMB003001 />)} />
				{/* 보물상자 오픈 */}
				<Route path="/BUPO001001" element={WithSuspense(<BUPO001001 />)} />
				{/* 보물상자 오픈 애니메이션 */}
				<Route path="/BUPO001002" element={WithSuspense(<BUPO001002 />)} />
				{/* 보물상자 오픈 결과 */}
				<Route path="/BUPO001003" element={WithSuspense(<BUPO001003 />)} />
				{/* 보물상자 구매 */}
				<Route path="/POIN001001" element={WithSuspense(<POIN001001 />)} />
				{/* 뉴스룸 */}
				<Route path="/NEWS001001" element={WithSuspense(<NEWS001001 />)} />
				{/* 거래소 */}
				<Route path="/EXCH001001" element={WithSuspense(<EXCH001001 />)} />
				{/* 배송요청 */}
				<Route path="/DLVR001001" element={WithSuspense(<DLVR001001 />)} />
				{/* 상세정보 */}
				<Route path="/DLVR002001" element={WithSuspense(<DLVR002001 />)} />
				{/* 배송지관리 */}
				{/* <Route path="/DLVR003001" element={WithSuspense(<DLVR003001 />}) /> */}
				{/* 마이페이지 */}
				<Route path="/MYME001001" element={WithSuspense(<MYME001001 />)} />
				{/* 내 정보 관리 */}
				<Route path="/MYME001002" element={WithSuspense(<MYME001002 />)} />
				{/* 내보물함 */}
				<Route path="/MYME002001" element={WithSuspense(<MYME002001 />)} />
				{/* 보물 */}
				<Route path="/TRSR001001" element={WithSuspense(<TRSR001001 />)} />
				{/* 분해하기 */}
				<Route path="/UPGD001001" element={WithSuspense(<UPGD001001 />)} />
				{/* 공지사항 */}
				<Route path="/HELP001001" element={WithSuspense(<HELP001001 />)} />
				{/* 이벤트 */}
				<Route path="/HELP002001" element={WithSuspense(<HELP002001 />)} />
				{/* 이용 내역 */}
				<Route path="/HELP003001" element={WithSuspense(<HELP003001 />)} />
				{/* 문의 내역 */}
				<Route path="/HELP004001" element={WithSuspense(<HELP004001 />)} />
				{/* 문의하기 */}
				<Route path="/HELP004002" element={WithSuspense(<HELP004002 />)} />
				{/* 나의 문의 */}
				<Route path="/HELP004003" element={WithSuspense(<HELP004003 />)} />
				{/* 알림설정 */}
				<Route path="/HELP005001" element={WithSuspense(<HELP005001 />)} />
				{/* 알림내역 */}
				<Route path="/HELP005002" element={WithSuspense(<HELP005002 />)} />
				{/* 자주 묻는 질문 */}
				<Route path="/HELP006001" element={WithSuspense(<HELP006001 />)} />
				{/* 약관 */}
				<Route path="/HELP007001" element={WithSuspense(<HELP007001 />)} />
				{/* 약관(서비스 이용약관) */}
				<Route path="/HELP007002" element={WithSuspense(<HELP007002 />)} />
				{/* 약관(서비스 개인정보취급방침) */}
				<Route path="/HELP007003" element={WithSuspense(<HELP007003 />)} />
				{/* 이용가이드 */}
				<Route path="/HELP008001" element={WithSuspense(<HELP008001 />)} />
				{/* Not found */}
				<Route path="*" element={WithSuspense(<NOTI001001 />)} />
			</Routes>
			<NOTI001001 />
			<TRSR001002 />
			{/* 업데이트 안내 */}
			<Modal
				isOpen={isOpenNeedUpdate}
				contents={
					<>
						<h2>
							<S.headline_4>Pengingat Pembaruan Versi Terbaru Aplikasi</S.headline_4>
						</h2>
						<p>
							<S.bodyText_M_long color={S.color.gray900}>
								Gopangers, versi terbaru aplikasi kami sudah tersedia.{`\n`}Yuk perbarui aplikasi Gopang Anda menjadi
								versi terbaru. Supaya kalian tidak ketinggalan fitur dan promo terbaru dari kami! 
							</S.bodyText_M_long>
						</p>
					</>
				}
				buttonTextL="OK"
				buttonActionL={() => {
					setOpenNeedUpdate(false);
					window.open(needUpdateUrl.current, '_blank');
				}}
			/>
			{/* 이미지 크게보기 */}
			<ImageLargeView
				isOpen={isOpenImageLargeView}
				close={() => setOpenImageLargeView(false)}
				$url={imageLargeViewUrl}
			/>
			{/* 최초 프로필 설정 */}
			<SetProfile
				isOpen={isOpenSetProfile}
				close={() => {
					setOpenSetProfile(false);
					// 회원가입시 광고성 푸시 동의 하지 않은 경우 생성
					!pushSendYn && setOpenPush(true);
				}}
			/>
			<Toast />
			{/* 이용가이드 */}
			<Guide
				isOpen={isOpenGuide}
				close={() => {
					setOpenGuide(false);
				}}
			/>
			{/* 알림 동의 팝업 */}
			<Modal
				isOpen={isOpenPush}
				contents={
					<>
						<h2>
							<S.headline_4>Halo, rasakan kebahagiaan{'\n'}bersama GOPANG!</S.headline_4>
						</h2>
						<p>
							<S.bodyText_M_long color={S.color.gray800}>
								Kami akan mengirimkan notifikasi untuk{'\n'}
								membantu Anda dalam menggunakan GOPANG
							</S.bodyText_M_long>
						</p>
						<S.popupBox>
							<S.controlsWrap
								style={{
									marginBottom: '0.8rem',
									alignItems: 'center',
								}}>
								<S.checkbox
									// 체크 여부에 따라 동의 여부 업데이트
									checked={changePushSendYn}
									id="pushAgree"
									name={'pushAgree'}
									type="checkbox"
									size={'L'}
									onChange={(e) => setChangePushSendYn(!changePushSendYn)}
								/>
								<label htmlFor="pushAgree">
									<S.bodyText_M>Kirimkan notifikasi event/update.</S.bodyText_M>
								</label>
							</S.controlsWrap>
							<S.bodyText_S color={S.color.gray700}>
								Anda perlu mengaktifkan pengaturan notifikasi di perangkat Anda untuk menerima notifikasi ini.
							</S.bodyText_S>
						</S.popupBox>
					</>
				}
				icon={'bell'}
				close={() => alertSetting(false)}
				buttonTextL={'Setuju'}
				buttonActionL={() => alertSetting(changePushSendYn)}
			/>
			{/* 푸시동의 */}
			<AlertSetting
				isOpen={isOpenAlertSetting}
				close={() => setOpenAlertSetting(false)}
				contents={alertSettingContents}
			/>
			{/* 업데이트 중 강제 종료하지 마십시오. */}
			<Modal
				isOpen={isOpenUpdate}
				contents={
					<>
						<h2>
							<S.headline_4>Pembaruan Aplikasi Sedang{'\n'}Berlangsung.</S.headline_4>
						</h2>
						<p>
							<S.bodyText_M_long color={S.color.gray800}>
								Mohon jangan tutup aplikasi agar pembaruan berjalan lancar.
							</S.bodyText_M_long>
						</p>
						<p>
							<S.bodyText_S color={S.color.error}>
								Tunggu hingga pembaruan selesai. Aplikasi akan dimulai ulang secara otomatis.
							</S.bodyText_S>
						</p>
					</>
				}
				icon={'rotate'}
			/>
			{/* 로그인 안내 */}
			<Modal
				isOpen={isOpenLogin}
				contents={
					<>
						<h2>
							<S.headline_4>Login diperlukan</S.headline_4>
						</h2>
						<p>
							<S.bodyText_M_long color={S.color.gray800}>
								Layanan ini membutuhkan login untuk digunakan. Silakan log in terlebih dahulu untuk menggunakan layanan
								ini.
							</S.bodyText_M_long>
						</p>
					</>
				}
				buttonTextL={'Kembali'}
				buttonActionL={() => setOpenLogin(false)}
				buttonTextR={'Log-in'}
				buttonActionR={() => {
					setOpenLogin(false);
					navigate('/MEMB001001');
				}}
			/>
			{/* 중복 로그인 */}
			<Modal
				isOpen={isOpenOverlap}
				contents={
					<>
						<h2>
							<S.headline_4>Perhatian! Login di Perangkat Lain</S.headline_4>
						</h2>
						<p>
							<S.bodyText_M_long color={S.color.gray800}>
								Akun Anda terdeteksi log-in dari perangkat lain. Apakah Anda ingin keluar dari perangkat lain dan log-in
								di perangkat ini?
							</S.bodyText_M_long>
						</p>
					</>
				}
				buttonTextL="Batal"
				buttonActionL={() => setOpenOverlap(false)}
				buttonTextR="OK"
				buttonActionR={() => {
					setOpenOverlap(false);
					navigate('/MEMB001001');
				}}
			/>
			{/* 재가입 제한 */}
			<Modal
				isOpen={isOpenAlert}
				contents={
					<>
						<h2>
							<S.headline_4>Pendaftaran Diblokir</S.headline_4>
						</h2>
						<p>
							<S.bodyText_M_long color={S.color.gray900}>
								Anda tidak dapat mendaftar ulang dengan akun yang telah dihapus selama 60 hari.
							</S.bodyText_M_long>
						</p>
						<p>
							<S.bodyText_M_long color={S.color.gray700}>
								Tanggal bisa mendaftar kembali : {Util.dateFormat(canJoinDt, 'date')}
							</S.bodyText_M_long>
						</p>
					</>
				}
				buttonTextL="OK"
				buttonActionL={() => setOpenAlert(false)}
			/>
			{/* 결제 취소(상자구매) */}
			<Modal
				isOpen={isOpenPaymentCancel}
				contents={
					<>
						<h2>
							<S.headline_4>Batalkan pembayaran</S.headline_4>
						</h2>
						<p>
							<S.bodyText_M_long color={S.color.gray800}>Pembayaran dibatalkan</S.bodyText_M_long>
						</p>
					</>
				}
				buttonTextL="Konfirmasi"
				buttonActionL={() => setOpenPaymentCancel(false)}
			/>
			{/* 앱 설정 이동 */}
			<Modal
				isOpen={isOpenSetting}
				contents={
					<>
						<h2>
							<S.headline_4>Pengaturan Lokasi</S.headline_4>
						</h2>
						<p>
							<S.bodyText_M_long color={S.color.gray800}>
								Untuk pengaturan lokasi, aplikasi ini memerlukan izin akses lokasi. Silakan aktifkan di [Pengaturan].
							</S.bodyText_M_long>
						</p>
					</>
				}
				buttonTextL="Batal"
				buttonActionL={() => setOpenSetting(false)}
				buttonTextR="Buka Pengaturan"
				buttonActionR={() => {
					// setGoogleMap(false);
					setOpenSetting(false);
					postMessage('SETTING');
				}}
			/>
		</>
	);
};

export default App;
